import { useState } from "react";
import { ArrowRight, X, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { submitTectorBridge } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Natural next-step block: reads as a sibling section, not a banner ad.
 * Positioned after the conflict flags — the setup for why Tector matters.
 */
export default function TectorBridge({ caseId }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const valid = name.trim().length > 1 && EMAIL_RE.test(email.trim());

  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitTectorBridge({
        name: name.trim(),
        email: email.trim(),
        case_id: caseId,
      });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => {
      setDone(false);
      setError(null);
      setName("");
      setEmail("");
    }, 200);
  };

  return (
    <>
      <section
        data-testid="tector-bridge"
        className="relative rounded-2xl border border-white/10 bg-[#12181A] overflow-hidden"
      >
        {/* Subtle Tector-green wash — on-brand, not a banner. */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.09]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 85% 15%, #35D07F 0%, transparent 55%)",
          }}
        />
        <div className="relative p-6 sm:p-10">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
            What's next
          </div>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white leading-[1.1] max-w-3xl">
            ScopeGrid scoped your research.{" "}
            <span className="text-[#35D07F]">Tector runs it.</span>
          </h2>
          <p className="mt-4 text-[15px] sm:text-base text-[#A0AAB2] leading-relaxed max-w-3xl">
            You have the questions and a first pass at the answers — including
            where sources disagree. Tector answers every question across your
            sources, keeps the full source trail, flags every contradiction, and
            weighs which source to trust so your findings are defensible. And it
            remembers all of it for your next project.
          </p>
          <div className="mt-6">
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="tector-bridge-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#35D07F] hover:bg-[#2CBB70] text-black font-semibold h-11 px-6 transition-colors"
            >
              Get early access to Tector
              <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </section>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={close}
          data-testid="tector-bridge-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-2xl border border-[#35D07F]/40 bg-[#12181A] p-6 sm:p-8"
          >
            <button
              onClick={close}
              aria-label="Close"
              data-testid="tector-bridge-close"
              className="absolute top-3 right-3 h-8 w-8 inline-flex items-center justify-center rounded-full text-white/50 hover:text-white hover:bg-white/5"
            >
              <X className="h-4 w-4" />
            </button>

            {!done ? (
              <>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
                  Tector · early access
                </div>
                <h3 className="mt-2 text-2xl font-bold tracking-tight text-white">
                  Answer every question, not just the top 5.
                </h3>
                <p className="mt-2 text-sm text-[#A0AAB2] leading-relaxed">
                  Add your work email and we'll invite you to the private beta
                  as spots open. No spam.
                </p>

                <div className="mt-5 space-y-3">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    data-testid="tector-bridge-name"
                    className="bg-[#0B0F10] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#35D07F]/50 h-11"
                  />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    data-testid="tector-bridge-email"
                    className="bg-[#0B0F10] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#35D07F]/50 h-11"
                  />
                  <Button
                    onClick={submit}
                    disabled={!valid || submitting}
                    data-testid="tector-bridge-submit"
                    className="w-full rounded-full bg-[#35D07F] hover:bg-[#2CBB70] text-black font-semibold h-11 disabled:opacity-40"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Sending…
                      </>
                    ) : (
                      "Request early access"
                    )}
                  </Button>
                  {error && (
                    <div
                      className="text-sm text-[#FF5252]"
                      data-testid="tector-bridge-error"
                    >
                      {error}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div
                className="text-center py-4"
                data-testid="tector-bridge-done"
              >
                <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#35D07F] text-black">
                  <Check className="h-6 w-6" strokeWidth={3} />
                </div>
                <h3 className="mt-4 text-xl font-bold text-white tracking-tight">
                  You're on the list.
                </h3>
                <p className="mt-2 text-sm text-[#A0AAB2]">
                  We'll email you when Tector opens up.
                </p>
                <Button
                  onClick={close}
                  className="mt-6 rounded-full bg-white/5 hover:bg-white/10 text-white border border-white/15 h-10 px-6"
                >
                  Back to results
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
